'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { Briefcase, HeartPulse, Building2, Leaf, GraduationCap, Scale, Wrench, CircleDollarSign } from 'lucide-react';
import { fetchCategorias, type CategoriaApi } from '@/services/catalogos';
import { fetchResultadosPublicos, type ResultadoPublicoApi } from '@/services/indicadores';



const getCategoryMeta = (name: string) => {
  const normName = name.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  
  if (normName.includes('econom') || normName.includes('trabajo') || normName.includes('actividad')) {
    return { icon: Briefcase, color: 'bg-[#3B82F6]', bg: 'bg-[#EFF6FF]', text: 'text-[#1D4ED8]' };
  }
  if (normName.includes('infraestructura') || normName.includes('vivienda') || normName.includes('construc')) {
    return { icon: Building2, color: 'bg-[#8B5CF6]', bg: 'bg-[#F5F3FF]', text: 'text-[#6D28D9]' };
  }
  if (normName.includes('ambiente') || normName.includes('ecolog') || normName.includes('clima')) {
    return { icon: Leaf, color: 'bg-[#10B981]', bg: 'bg-[#ECFDF5]', text: 'text-[#047857]' };
  }
  if (normName.includes('salud') || normName.includes('sanitar') || normName.includes('medic')) {
    return { icon: HeartPulse, color: 'bg-[#F43F5E]', bg: 'bg-[#FFF1F2]', text: 'text-[#BE123C]' };
  }
  if (normName.includes('educa') || normName.includes('escuel') || normName.includes('universi')) {
    return { icon: GraduationCap, color: 'bg-[#F59E0B]', bg: 'bg-[#FFFBEB]', text: 'text-[#B45309]' };
  }
  if (normName.includes('gober') || normName.includes('transparencia') || normName.includes('institu')) {
    return { icon: Scale, color: 'bg-[#64748B]', bg: 'bg-[#F8FAFC]', text: 'text-[#334155]' };
  }
  if (normName.includes('servicio') || normName.includes('agua') || normName.includes('energia') || normName.includes('luz')) {
    return { icon: Wrench, color: 'bg-[#0EA5E9]', bg: 'bg-[#F0F9FF]', text: 'text-[#0369A1]' };
  }
  return { icon: CircleDollarSign, color: 'bg-[#64748B]', bg: 'bg-[#F8FAFC]', text: 'text-[#334155]' };
};

export default function CategoriasPage() {
  const [categories, setCategories] = useState<CategoriaApi[]>([]);
  const [results, setResults] = useState<ResultadoPublicoApi[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const loadData = async () => {
      try {
        const [cats, res] = await Promise.all([
          fetchCategorias(),
          fetchResultadosPublicos({ per_page: 200 })
        ]);
        if (active) {
          if (cats.length > 0) setCategories(cats);
          if (res.length > 0) setResults(res);
          setLoading(false);
        }
      } catch (err) {
        console.error('Failed to load categories explorer data', err);
        if (active) setLoading(false);
      }
    };
    loadData();
    return () => { active = false; };
  }, []);

  const getIndicatorCount = (categoryName: string) => {
    const categoryResults = results.filter(r => r.categoria === categoryName);
    const uniqueCodes = new Set(categoryResults.map(r => r.codigo_interno));
    return uniqueCodes.size;
  };

  const displayCategories = categories.map((cat) => {
    const meta = getCategoryMeta(cat.nombre);
    const count = getIndicatorCount(cat.nombre);
    return {
      id: String(cat.id),
      name: cat.nombre,
      count: count,
      description: cat.descripcion || `Fichas, trazabilidad e indicadores temáticos de ${cat.nombre}.`,
      ...meta
    };
  });

  return (
    <main className="max-w-7xl mx-auto w-full px-8 py-16 flex-1">
      <div className="mb-12">
        <h2 className="text-4xl font-[family-name:var(--font-righteous)] text-[#002B5B] mb-4">
          Explorar por Categorías
        </h2>
        <p className="text-lg text-gray-600 max-w-3xl">
          El Sistema de Indicadores de Ciudad Inteligente y Sostenible (SICIS) clasifica la información estratégica de Luján en áreas temáticas estandarizadas según la norma ISO 37120.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {displayCategories.map((cat) => {
          const Icon = cat.icon;
          return (
            <Link href="/indicadores" key={cat.id} className="group">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 relative overflow-hidden hover:shadow-md transition-all flex flex-col h-full">
                <div className={`absolute top-0 left-0 right-0 h-2 ${cat.color}`}></div>
                
                <div className={`w-14 h-14 rounded-2xl ${cat.bg} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                  <Icon className={`w-7 h-7 ${cat.text}`} />
                </div>
                
                <h3 className="font-bold text-xl text-gray-800 mb-2">{cat.name}</h3>
                <p className="text-gray-500 text-sm mb-6 flex-1">
                  {cat.description}
                </p>
                
                <div className="flex items-center justify-between border-t border-gray-100 pt-4 mt-auto">
                  <span className="text-sm font-bold text-gray-400">
                    {cat.count} indicadores
                  </span>
                  <span className={`text-sm font-bold ${cat.text} flex items-center group-hover:translate-x-1 transition-transform`}>
                    Explorar <span className="ml-1">→</span>
                  </span>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </main>
  );
}
